import { useConversation } from '@elevenlabs/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { convertJapaneseNumbersToRoman } from '@/utils/japaneseNumberConverter';

export enum CallState {
  INCOMING = 'incoming',
  ACTIVE = 'active',
  ENDED = 'ended',
}

export enum LoadingState {
  INITIAL = 'initial',
  CONNECTING = 'connecting',
  READY = 'ready',
  ACTIVE = 'active',
  ENDING = 'ending',
  ERROR = 'error',
}

export interface UseOnboardingAgentCallProps {
  userId?: string;
  onCallEnded?: () => void;
  persona?: string;
}

export interface Transcript {
  source: string;
  message: string;
  timestamp: Date;
}

// Jago Coffee Persona Configuration
interface PersonaConfig {
  voiceId: string;
  avatar: string;
  name: string;
  systemPrompt: string;
}

const JAGO_PERSONA: PersonaConfig = {
  voiceId: 'gmnazjXOFoOcWA59sd5m',
  avatar:
    'https://dopmo1eihgbgm.cloudfront.net/68b81c5527352545f9c2fcd6/indonesia-woman.png',
  name: 'Sari Jago Coffee Trainer',
  systemPrompt: `Anda adalah Sari, trainer Jago Coffee yang akan menjalankan training untuk Mobile Barista. Anda akan menanyakan 9 pertanyaan tentang Jago Coffee secara berurutan tanpa memberikan jawaban atau feedback pada setiap pertanyaan.

PENTING:
- Jangan memberikan feedback benar/salah pada jawaban
- Jangan ulangi pertanyaan yang sama
- Setelah user menjawab, langsung lanjut ke pertanyaan berikutnya
- Gunakan transisi singkat seperti "Terima kasih" atau "Baik, lanjut"

【Kepribadian & Cara Bicara】
- Ramah, profesional, trainer Indonesia
- Bahasa Indonesia yang jelas
- Supportif tapi tetap efisien
- Langsung ke pertanyaan tanpa bertele-tele

【Daftar Pertanyaan Berurutan】
FASE PERKENALAN:
"Selamat datang di Jago Coffee Training! Saya adalah trainer Anda. Saya akan menanyakan 9 pertanyaan tentang Jago Coffee untuk menguji pemahaman Anda.

Kita akan membahas 4 topik: Tentang Jago Coffee, Grooming, Produk, dan Pengenalan Cart.

Mari kita mulai dengan pertanyaan pertama."

PERTANYAAN 1: TENTANG JAGO COFFEE
"Pertanyaan pertama tentang Jago Coffee.

Apa itu Jago Coffee atau PT. Satuan Teknologi Berjalan?"

[TUNGGU JAWABAN, LALU LANGSUNG LANJUT]

PERTANYAAN 2: TENTANG JAGO COFFEE
"Pertanyaan kedua.

Berapa jumlah seragam yang kamu terima sebagai Mobile Barista?"

PERTANYAAN 3: TENTANG JAGO COFFEE
"Pertanyaan ketiga.

Apa keunggulan utama Jago Coffee dalam pelayanan?"

TRANSISI KE GROOMING:
"Sekarang kita beralih ke topik Grooming - standar penampilan untuk Mobile Barista."

PERTANYAAN 4: GROOMING
"Pertanyaan keempat tentang grooming.

Apa saja yang termasuk grooming yang baik bagi seorang Mobile Barista?"

PERTANYAAN 5: GROOMING
"Pertanyaan kelima.

Apa hal terpenting dalam berpenampilan sebagai Mobile Barista?"

TRANSISI KE PRODUK:
"Selanjutnya kita masuk ke topik Produk - tentang menu dan kualitas minuman Jago Coffee."

PERTANYAAN 6: PRODUK
"Pertanyaan keenam tentang produk.

Apa yang akan terjadi jika produk dibiarkan terlalu lama di suhu ruang?"

PERTANYAAN 7: PRODUK
"Pertanyaan ketujuh.

Produk apa saja yang menggunakan Kopi Robusta?"

TRANSISI KE PENGENALAN CART:
"Topik terakhir adalah Pengenalan Cart - tentang penggunaan dan perawatan sepeda listrik Jago Coffee."

PERTANYAAN 8: PENGENALAN CART
"Pertanyaan kedelapan tentang cart.

Berapa jarak tempuh jika battery terisi full?"

PERTANYAAN 9: PENGENALAN CART
"Pertanyaan terakhir.

Apabila terdapat cart yang bermasalah, apa yang harus kamu lakukan?"

FASE PENYELESAIAN:
"Terima kasih! Anda telah menyelesaikan semua 9 pertanyaan dalam training Jago Coffee.

Training selesai. Mari kita lihat hasil Anda."

【Aturan Perilaku】
- Antar Pertanyaan: Tidak ada feedback pada jawaban individu, hanya "Terima kasih" atau "Baik, lanjut ke pertanyaan berikutnya"
- Transisi yang halus antar topik
- Tempo konsisten - tunggu jawaban sebelum lanjut
- Jika tidak ada respon setelah beberapa saat: "Silakan berikan jawaban Anda"
- Tetap bergerak maju, jangan evaluasi performa sebelumnya

【PENTING】
- Hanya tanyakan pertanyaan, JANGAN BERIKAN JAWABAN
- Jangan beri feedback benar/salah pada setiap jawaban
- Ikuti urutan pertanyaan dengan ketat
- Gunakan bahasa Indonesia yang ramah dan profesional
- Terima jawaban dalam bentuk apa pun (tidak ada pilihan ganda)`,
};

// Onboarding Agent Configuration
const ONBOARDING_CONFIG = {
  agentId: 'agent_0501k47f86vcf1ytx0yggdk9kned',
  language: 'id',
  initialPrompt: 'Selamat datang di Jago Coffee Training!',
};

export const useOnboardingAgentCall = ({
  userId,
  onCallEnded,
  persona = 'jago-trainer',
}: UseOnboardingAgentCallProps) => {
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.INITIAL,
  );
  const [error, setError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>(CallState.INCOMING);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState<boolean>(false);

  // Call timer state
  const [callDuration, setCallDuration] = useState<number>(0);
  const startTimeRef = useRef<number>(0);
  const callTimerRef = useRef<any>(null);

  // Transcript state
  const [transcript, setTranscript] = useState<Transcript[]>([]);
  
  // Speaking state - manage our own since ElevenLabs hook doesn't always work correctly
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);

  const conversation = useConversation();

  // Agent info for Jago Coffee
  const agentInfo = {
    profile: {
      name: 'Sari - Jago Coffee Trainer',
      role: 'Training Supervisor',
      avatar: JAGO_PERSONA.avatar,
    },
  };

  useEffect(() => {
    // Set ready state on mount
    setLoadingState(LoadingState.READY);
  }, []);

  useEffect(() => {
    if (callState === CallState.ACTIVE) {
      // Start call timer
      callTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }

    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [callState]);

  const handleAnswerCall = async () => {
    try {
      setLoadingState(LoadingState.CONNECTING);

      // Generate session ID
      const newSessionId = `onboarding_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      setSessionId(newSessionId);

      // Request microphone access for multimodal conversation
      await navigator.mediaDevices.getUserMedia({ audio: true });

      // Start conversation with multimodal configuration
      const conversationId = await conversation.startSession({
        agentId: ONBOARDING_CONFIG.agentId,
        connectionType: 'webrtc',
        overrides: {
          agent: {
            prompt: {
              prompt: JAGO_PERSONA.systemPrompt,
            },
            firstMessage: ONBOARDING_CONFIG.initialPrompt,
          },
          tts: {
            voiceId: JAGO_PERSONA.voiceId,
          },
        },
        onConnect: () => {
          console.log('Connected to Onboarding conversation');
          startTimeRef.current = Date.now();
          setLoadingState(LoadingState.ACTIVE);
          setCallState(CallState.ACTIVE);
        },
        onError: (error) => {
          console.error('Onboarding Conversation error:', error);
          setError(error || 'Connection Error');
          setLoadingState(LoadingState.ERROR);
        },
        onMessage({ source, message }) {
          console.log('Onboarding Message:', source, message);

          // Convert Japanese numbers to Roman numerals for better readability if needed
          const processedMessage = convertJapaneseNumbersToRoman(message);

          setTranscript((prev) => [
            ...prev,
            {
              source,
              message: processedMessage,
              timestamp: new Date(),
            },
          ]);

          // Update speaking state based on message source
          if (source !== 'user') {
            // Agent/AI starts speaking
            setIsSpeaking(true);
            
            // Stop speaking after a delay (estimated based on message length)
            const estimatedDuration = Math.max(2000, message.length * 100); // 100ms per character, minimum 2s
            setTimeout(() => {
              setIsSpeaking(false);
            }, estimatedDuration);
          } else {
            // User is speaking/typing, agent stops
            setIsSpeaking(false);
          }
        },
      });

      console.log('Onboarding Conversation started with ID:', conversationId);
    } catch (error) {
      console.error('Error starting Onboarding call:', error);
      setError('Failed to start Onboarding training call');
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleEndCall = async () => {
    try {
      setLoadingState(LoadingState.ENDING);

      if (conversation) {
        await conversation.endSession();
      }

      // End call
      setCallState(CallState.ENDED);

      toast.success('Training telah selesai');

      // Call the callback if provided
      if (onCallEnded) {
        onCallEnded();
      }
    } catch (error) {
      console.error('Error ending Onboarding call:', error);
      toast.error('Gagal mengakhiri training');
      setCallState(CallState.ENDED);
    } finally {
      setLoadingState(LoadingState.READY);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (conversation && callState === CallState.ACTIVE) {
      try {
        // Add user message to transcript first
        setTranscript((prev) => [
          ...prev,
          {
            source: 'user',
            message: message,
            timestamp: new Date(),
          },
        ]);

        // Then send the message
        conversation.sendUserMessage(message);
      } catch (error) {
        console.error('Error sending message:', error);
        toast.error('Gagal mengirim pesan');
      }
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const retryInitialization = () => {
    setError(null);
    setLoadingState(LoadingState.READY);
  };

  return {
    loadingState,
    error,
    callState,
    sessionId,
    agentInfo,
    isMuted,
    callDuration,
    transcript,
    isSpeaking,
    handleAnswerCall,
    handleEndCall,
    handleSendMessage,
    formatTime,
    retryInitialization,
  };
};
